import { head, includes, keys, size, without } from "lodash";
import { DateMath, Interval } from "@linkfluence/models/date";
import { DemographicRange } from "@linkfluence/models/demographic-range";
import { Facet } from "@linkfluence/models/facet";
import { Gender } from "@linkfluence/models/gender";
import { Metric } from "@linkfluence/models/metric";
import { Platform } from "@linkfluence/models/platform";
import { Timezone } from "@linkfluence/models/timezone";
import { AISummaries } from "./ai";
import { Clause } from "./clause";
import { DatavizTypeAI } from "./dataviz";
import { FullInfluencer, SearchableInfluencer } from "./influencer";
import { Post } from "./posts";

type MetricsCounts = Partial<Record<Metric, number>>;

type BaseSearchData = {
  hits: Post[];
  took: number;
  total: number;
  id?: string;
};

// INFLUENCERS
export interface SearchFragmentUsers {
  request: BaseSearchParams & {
    aggs: {
      users: {
        // controls the metrics computed by the aggregation
        metrics: Metric[];
        // max authors returned (max: 1000, default 25)
        size?: number;
        sort?: {
          // How to sort aggregation results, values: post, rank, pageRank, name, or any metric
          on: SortInfluencer;
          order: Sort["order"];
        };
      };
    };
  };
  data?: BaseSearchData & {
    aggs: {
      users: {
        values: SearchableInfluencer[];
      };
    };
  };
  type?: SearchFragmentType;
}

// DATE/METRICS
export interface SearchFragmentDateMetrics<M extends Metric[] = Metric[]> {
  request: AggsSearchParams<
    {
      date: {
        interval: Interval;
        metrics: M;
      };
    },
    M
  >;
  data?: AggsSearchData<{
    date: {
      values: Array<{ key: string; counts: MetricsCounts }>;
    };
  }>;
  type?: SearchFragmentType;
}

export interface SearchFragmentDateTerm<M extends Metric[] = Metric[]> {
  request: AggsSearchParams<
    {
      date: {
        interval: Interval;
        metrics: M;
        aggs: { date: never } & {
          [key: string]: {
            metrics: M;
            size?: number;
            focuses?: number[];
            dashboard?: number;
            sort?: {
              on: Metric;
              order: Sort["order"];
            };
          };
        };
      };
    },
    M
  >;
  data?: AggsSearchData<{
    date: {
      values: Array<{
        key: string;
        counts: MetricsCounts;
        raw_doc_count?: number;
        aggs: {
          [term: string]: {
            values: Array<{ key: string; counts: MetricsCounts }>;
          };
        };
      }>;
    };
  }>;
  type?: SearchFragmentType;
}

export const CloudTerms = [
  Facet.EmojiAnnotations,
  Facet.EmojiCharts,
  Facet.Hashtags,
  Facet.Keywords,
  Facet.Affects,
  Facet.Mentions,
  Facet.Entities,
  Facet.SignificantHashtags,
  Facet.SignificantMentions,
  Facet.SignificantEntities,
  Facet.SignificantNamedEntities,
  Facet.EmergingHashtags,
  Facet.EmergingMentions,
  Facet.EmergingNamedEntities,
] as const;

export type CloudTerm = typeof CloudTerms[number];

export enum PivotFields {
  Pivot = "pivot",
  Against = "against",
}

export type PivotTerms = PivotFields.Pivot | PivotFields.Against;

export interface SearchFragmentDemographic<M extends Metric[] = Metric[]> {
  request: AggsSearchParams<
    {
      genders: {
        metrics: M;
        aggs: {
          age: {
            metrics: M;
          };
        };
      };
    },
    M
  >;
  data?: AggsSearchData<{
    genders: {
      values: GenderSearchData[];
    };
  }>;
  type?: SearchFragmentType;
}

export const GeoTerms = [Facet.Towns, Facet.Regions, Facet.Countries] as const;

export type GeoTerm = typeof GeoTerms[number];

export function isGeoTerm(term: string): term is GeoTerm {
  return includes(GeoTerms, term);
}

export interface SearchFragmentGeoPlace {
  key: string;
  counts: MetricsCounts;
  isoCode?: string;
  location?: {
    name: string;
    lang: string;
    lat: number;
    lng: number;
    population: number;
  };
}

export interface SearchFragmentGeo {
  request: AggsSearchParams<
    Partial<
      Record<
        GeoTerm,
        {
          metrics: Metric[];
          sort: {
            on: Metric;
            order: Sort["order"];
          };
          size?: number;
        }
      >
    >,
    Metric[]
  >;
  data?: AggsSearchData<
    Partial<
      Record<
        GeoTerm,
        {
          values: SearchFragmentGeoPlace[];
        }
      >
    >
  >;
  type?: SearchFragmentType;
}

export interface SearchFragmentClassification<M extends Metric[] = Metric[]> {
  request: AggsSearchParams<
    {
      contentClassification: {
        metrics: M;
        size?: number;
        sort?: {
          on: Metric;
          order: Sort["order"];
        };
      };
    },
    M
  >;
  data?: AggsSearchData<{
    contentClassification: {
      values: Array<{
        key: string;
        counts: MetricsCounts;
      }>;
    };
  }>;
  type?: SearchFragmentType;
}

export interface SearchFragmentTerm<M extends Metric[] = Metric[]> {
  request: AggsSearchParams<
    { date: never } & {
      [key in string]: {
        focuses?: number[];
        dashboard?: number;
        metrics: M;
        size?: number;
        sort?: {
          on: Metric;
          order: Sort["order"];
        };
      };
    },
    M
  >;
  data?: AggsSearchData<{
    [key: string]: {
      method?: "exact" | "approximate";
      values: Array<{
        key: string;
        counts: MetricsCounts;
        label?: string;
      }>;
    };
  }>;
  type?: SearchFragmentType;
}

export interface SearchFragmentEmv<M extends Metric[] = Metric[]> {
  request: AggsSearchParams<
    {
      date: {
        metrics: M;
        interval: Interval;
        aggs: {
          platforms: {
            metrics: M;
            aggs: {
              countries: {
                metrics: M;
              };
            };
          };
        };
      };
    },
    M
  >;
  data?: AggsSearchData<{
    date: {
      values: {
        key: string;
        raw_doc_count: number;
        counts: Record<Metric, number>;
        aggs: {
          platforms: {
            values: {
              key: Platform;
              raw_doc_count: number;
              counts: Record<Metric, number>;
              aggs: {
                countries: {
                  values: {
                    key: string;
                    raw_doc_count: number;
                    counts: Record<Metric, number>;
                  }[];
                };
              };
            }[];
          };
        };
      }[];
    };
  }>;
  type?: SearchFragmentType;
}

export interface AIFragment {
  request: BaseSearchParams;
  data?: AISummaries;
  type?: SearchFragmentType;
  analysis: {
    type: DatavizTypeAI;
    useInsightPageAIContext: boolean;
    includeNegativeAnalysis: boolean; // for "general-analysis" only
  };
  context?: string; // Ip description
}

export interface SearchFragmentPosts {
  request: BaseSearchParams;
  data?: BaseSearchData;
  type?: SearchFragmentType;
}

export interface SearchWarmupFragmentPosts {
  request: WarmupSearchParams;
  data?: BaseSearchData;
  type?: SearchFragmentType;
}

export type EmptyFragment = [];

export interface ClusterFragment {
  request: BaseSearchParams;
  data?: BaseSearchData;
  type?: SearchFragmentType;
}
export interface SearchFragmentPinnedPost {
  request: BaseSearchParams;
  data?: BaseSearchData;
  type?: SearchFragmentType;
}

export interface InfluencerFragment {
  authorId: string;
  type: "influencer";
  data?: FullInfluencer;
}

export interface SearchFragmentPivot<M extends Metric[] = Metric[]> {
  request: BaseSearchParams & {
    aggs: {
      [key: string]: {
        focuses?: number[];
        dashboard?: number;
        metrics: M;
        aggs: {
          [key: string]: {
            focuses?: number[];
            dashboard?: number;
            metrics: M;
          };
        };
      };
    };
  };
  data?: BaseSearchData & {
    aggs: {
      [term: string]: {
        values: Array<{
          key: string;
          counts: MetricsCounts;
          aggs: {
            [term: string]: {
              values: Array<{ key: string; counts: MetricsCounts }>;
            };
          };
        }>;
      };
    };
  };
  type?: SearchFragmentType;
}
interface GenderSearchData {
  key: Gender.Male | Gender.Female;
  aggs: {
    age: {
      values: Array<{ key: DemographicRange; counts: MetricsCounts }>;
    };
  };
  counts: MetricsCounts;
}

export type SearchFragmentWithAgg =
  | SearchFragmentDateMetrics
  | SearchFragmentDateTerm
  | SearchFragmentDemographic
  | SearchFragmentUsers
  | SearchFragmentGeo
  | SearchFragmentTerm
  | SearchFragmentPivot
  | SearchFragmentClassification
  | SearchFragmentEmv;

export type SearchFragment =
  | SearchFragmentWithAgg
  | SearchFragmentPosts
  | ClusterFragment;

export type GlobalFragment = SearchFragment | InfluencerFragment | AIFragment;

export type TimeUnitFragment =
  | SearchFragmentDateMetrics
  | SearchFragmentDateTerm;

export type SearchData = SearchFragment["data"];

export type Flag = "withRT" | "favorite";

interface DateRangeFilter {
  // match documents after this date
  after?: string | DateMath;
  // match documents before this date
  before?: string | DateMath;
  // same as before
  lt?: string | DateMath;
  // same as after
  gt?: string | DateMath;
}

type InnerClause = {
  // A valid query string
  query: string;
  // Same as query but compute a BM25 doc score to sort on. Useful for relevance search.
  scoredQuery: string;

  // Filter on author's birthdate
  birthDate: DateRangeFilter;
  // Filter on document creation date in the project
  created: DateRangeFilter;
  // Filter on document last update date
  updated: DateRangeFilter;
};

interface NotClause {
  not: Partial<InnerClause>;
}

export interface Clause2 {
  and: Array<Partial<InnerClause> | NotClause>[];
}

export interface Sort {
  // Default to date
  on?:
    | "date"
    | "radar.created"
    | "virality"
    | "relevance"
    | "rating"
    | "random"
    | Metric.Engagement
    | Metric.Impression
    | Metric.Reach;
  // Either asc (ascending) or desc (descending). Default to desc
  order?: "desc" | "asc";
}

export interface Pagination {
  // Number of hits returned, min 0, max 250 default to 20
  limit?: number;
  // Offset to control the page returned. Min 0, max 1000, default to 0
  start?: number;
  // Controls hits sort
  sort?: Sort;
}

export type SearchFragmentType = "cluster" | "posts" | "emv" | "ai-analysis";

export interface BaseSearchParams {
  // Query UID
  id?: string;
  // Composition of Clause objects to filter documents
  query: Clause;
  // Timezone hint to control how date time aggregations computes day, month etc.
  // If not provided fallback to user's timezone
  timezone?: Timezone;
  // Turn highlighting on, default to false. Highlights queries referenced in fctx
  highlight?: boolean;
  // A list of focus IDs which is used by highlighting and percolation modules
  // on every document returned by the API
  fctx?: number[];
  // Controls results pagination and sort
  pagination?: Pagination;
}
export interface WarmupSearchParams {
  id?: string;
  clause: Clause;
  timezone?: Timezone;
  hasHighLighting?: boolean;
  fctx?: number[];
  pagination?: Pagination;
}

type AggsSearchParams<T, M extends Metric[]> = BaseSearchParams & {
  aggs: T & {
    metrics?: { metrics: M };
  };
};

type AggsSearchData<T> = BaseSearchData & {
  aggs: T & {
    metrics?: {
      metrics: MetricsCounts;
    };
  };
};

export type SortInfluencer = "rank" | "pageRank" | "name" | "rating" | Metric;

export type SearchParams = SearchFragment["request"];

export function getTermFromAggs(
  aggs: SearchFragmentWithAgg["request"]["aggs"]
) {
  return head(without(keys(aggs), "metrics"));
}

export function getFragmentName(
  fragment: SearchFragment | EmptyFragment | InfluencerFragment | AIFragment
) {
  if (isEmptyFragment(fragment)) {
    return null;
  }
  if (fragment.type === "influencer") {
    return "influencer";
  }
  if (fragment.type === "cluster") {
    return "cluster";
  }
  if (fragment.type === "emv") {
    return "emv";
  }
  if (fragment.type === "ai-analysis") {
    return "ai-analysis";
  }
  const aggs = (fragment as SearchFragmentWithAgg)["request"]["aggs"];
  if (aggs && size(aggs)) {
    const term = getTermFromAggs(aggs);
    if (term === "date") {
      return aggs[term].aggs ? "date-term" : "date-metrics";
    } else if (term === "contentClassification") {
      return "classification";
    } else if (term === "genders") {
      return aggs[term].aggs && aggs[term].aggs.age
        ? "demographic"
        : aggs[term].aggs
        ? "pivot"
        : "term";
    } else if (term === "users") {
      return "users";
    } else if (aggs[term].aggs) {
      return "pivot";
    } else if (includes([Facet.Towns, Facet.Regions, Facet.Countries], term)) {
      return "geo";
    } else {
      return "term";
    }
  } else {
    return "posts";
  }
}

export function isSearchFragmentDateMetrics(
  fragment: GlobalFragment
): fragment is SearchFragmentDateMetrics {
  return getFragmentName(fragment) === "date-metrics";
}

export function isSearchFragmentTerm(
  fragment: GlobalFragment
): fragment is SearchFragmentTerm {
  return getFragmentName(fragment) === "term";
}

export function isSearchFragmentDateTerm(
  fragment: GlobalFragment
): fragment is SearchFragmentDateTerm {
  return getFragmentName(fragment) === "date-term";
}

export function isSearchFragmentGeo(
  fragment: GlobalFragment
): fragment is SearchFragmentGeo {
  return getFragmentName(fragment) === "geo";
}

export function isSearchFragmentPosts(
  fragment: GlobalFragment
): fragment is SearchFragmentPosts {
  return getFragmentName(fragment) === "posts";
}

export function isSearchFragmentClassification(
  fragment: GlobalFragment
): fragment is SearchFragmentClassification {
  return getFragmentName(fragment) === "classification";
}

export function isSearchFragmentPivot(
  fragment: GlobalFragment
): fragment is SearchFragmentPivot {
  return getFragmentName(fragment) === "pivot";
}

export function isSearchFragmentUsers(
  fragment: GlobalFragment
): fragment is SearchFragmentUsers {
  return getFragmentName(fragment) === "users";
}

export function isSearchFragmentDemographic(
  fragment: GlobalFragment
): fragment is SearchFragmentDemographic {
  return getFragmentName(fragment) === "demographic";
}

export function isSearchFragmentEmv(
  fragment: GlobalFragment
): fragment is SearchFragmentEmv {
  return getFragmentName(fragment) === "emv";
}

export function isAIFragment(fragment: GlobalFragment): fragment is AIFragment {
  return getFragmentName(fragment) === "ai-analysis";
}

export function isClusterFragment(
  fragment: GlobalFragment
): fragment is ClusterFragment {
  return getFragmentName(fragment) === "cluster";
}

export function isEmptyFragment(
  fragment: EmptyFragment | GlobalFragment
): fragment is EmptyFragment {
  if (!fragment) {
    return true;
  }
  if (Array.isArray(fragment)) {
    return fragment.length === 0;
  }
  return false;
}
export function isInfluencerFragment(
  fragment: EmptyFragment | GlobalFragment
): fragment is InfluencerFragment {
  return getFragmentName(fragment) === "influencer";
}
